import { useState, useEffect, useCallback, useRef } from 'react';
import forecastService, { type ForecastRain } from '../api/forecastService';
import type { EventWithCircuit } from '../api/eventsService';
import { getEventSource } from '../utils/eventSource';

export function useRainForecastData(selectedEvent: EventWithCircuit | null, refreshCounter: number = 0) {
  const [forecasts, setForecasts] = useState<ForecastRain[]>([]);
  const [loading, setLoading] = useState(false);
  const isFirstLoadRef = useRef(true);

  const fetchForecasts = useCallback(async (showLoading = true) => {
    if (!selectedEvent) {
      setForecasts([]);
      setLoading(false);
      return;
    }

    const isFirstLoad = isFirstLoadRef.current;
    if (showLoading && isFirstLoad) {
      setLoading(true);
    }

    try {
      const source = getEventSource(selectedEvent);
      const response = await forecastService.getForecastRain({
        season_year: selectedEvent.season_year,
        circuit_id: selectedEvent.circuit_id,
        start_date: selectedEvent.start_date,
        source,
      });

      setForecasts(response.forecasts || []);
      if (isFirstLoad) {
        isFirstLoadRef.current = false;
        setLoading(false);
      }
    } catch (err) {
      if (isFirstLoad) {
        console.error('Failed to fetch rain forecasts:', err);
        setForecasts([]);
        setLoading(false);
      } else {
        console.warn('Rain forecast silent refresh failed');
      }
    }
  }, [selectedEvent]);

  useEffect(() => {
    isFirstLoadRef.current = true;
    setForecasts([]);
    setLoading(false);
  }, [selectedEvent]);

  useEffect(() => {
    fetchForecasts(true);
  }, [fetchForecasts, refreshCounter]);

  // Drop stale forecasts immediately when the event is cleared (season change),
  // before the reset effect runs. Otherwise RainForecastHeader still resolves
  // the previous event's session_type against empty sessions.
  return { forecasts: selectedEvent ? forecasts : [], loading: selectedEvent ? loading : false };
}
